import { Heart, Star, Trophy, Zap, BarChart3 } from "lucide-react";
import { useEffect, useCallback, useMemo, useState, useRef } from "react";
import { useSettings, useHighScores } from "./hooks/useLocalStorage";
import { useGameState } from "./hooks/useGameState";
import { useProblemGenerator } from "./hooks/useProblemGenerator";
import { useGameSession } from "./hooks/useGameSession";
import { useStatistics } from "./hooks/useStatistics";
import { playSound, initializeAudio } from "./utils/sounds";
import { triggerConfetti, triggerCelebration } from "./utils/confetti";
import SetupScreen from "./components/SetupScreen";
import GameOverScreen from "./components/GameOverScreen";
import SettingsPanel from "./components/SettingsPanel";
import ResumeGameDialog from "./components/ResumeGameDialog";
import StatisticsModal from "./components/StatisticsModal";
import Timer from "./components/Timer";
import { TIMINGS, GAME_MODE_CONFIG } from "./config/gameConfig";

const Matematikspillet = () => {
  // Track max streak achieved during the game
  const [maxStreak, setMaxStreak] = useState(0);

  // Track saved session for resume functionality
  const [savedSession, setSavedSession] = useState(null);
  const [showResumeDialog, setShowResumeDialog] = useState(false);

  // Statistics modal state
  const [showStatistics, setShowStatistics] = useState(false);

  // Ref for answer input to manage focus
  const answerInputRef = useRef(null);

  // Custom hooks
  const { settings, toggleSound, toggleAnimations } = useSettings();
  const { highScores, addHighScore } = useHighScores();
  const statisticsHook = useStatistics();

  const {
    gameState,
    setupStep,
    playerName,
    selectedOperations,
    difficulty,
    gameMode,
    score,
    streak,
    level,
    lives,
    achievements,
    timeRemaining,
    setPlayerName,
    toggleOperation,
    setDifficulty,
    setGameMode,
    nextStep,
    prevStep,
    startGame: startGameState,
    addScore,
    incrementStreak,
    resetStreak,
    levelUp,
    loseLife,
    addAchievement,
    endGame,
    resetGame,
    removeFirstAchievement,
    setTimeRemaining,
  } = useGameState();

  const {
    currentProblem,
    userAnswer,
    setUserAnswer,
    feedback,
    showCelebration,
    generateNewProblem,
    checkAnswer,
  } = useProblemGenerator(difficulty, selectedOperations, {
    addScore,
    incrementStreak,
    resetStreak,
    levelUp,
    loseLife,
    addAchievement,
    endGame,
  }, statisticsHook.trackProblem);

  // Session management for auto-save
  const sessionData = useMemo(() => ({
    playerName,
    selectedOperations,
    difficulty,
    score,
    streak,
    level,
    lives,
    maxStreak,
    currentProblem,
  }), [playerName, selectedOperations, difficulty, score, streak, level, lives, maxStreak, currentProblem]);

  const { getSavedSession, clearSession } = useGameSession(gameState, sessionData);

  // Update max streak whenever streak changes
  useEffect(() => {
    if (streak > maxStreak) {
      setMaxStreak(streak);
    }
  }, [streak, maxStreak]);

  // Reset max streak when game resets
  useEffect(() => {
    if (gameState === 'setup') {
      setMaxStreak(0);
    }
  }, [gameState]);

  // Timer countdown for Time Attack mode
  useEffect(() => {
    const modeConfig = GAME_MODE_CONFIG[gameMode];

    if (gameState === 'playing' && modeConfig?.hasTimer && timeRemaining !== null) {
      if (timeRemaining <= 0) {
        // Time's up!
        playSound('gameOver', settings.soundEnabled);
        clearSession();

        // Add high score
        const noHighScore = modeConfig.noHighScore;
        if (!noHighScore) {
          addHighScore({
            playerName,
            score,
            level,
            difficulty,
            date: new Date().toISOString(),
          });
        }

        endGame();
        return;
      }

      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev === null || prev <= 0) return prev;
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameState, gameMode, timeRemaining, settings.soundEnabled, clearSession, addHighScore, playerName, score, level, difficulty, endGame, setTimeRemaining]);

  // Check for saved session on mount
  useEffect(() => {
    const session = getSavedSession();
    if (session && gameState === 'setup') {
      setSavedSession(session);
      setShowResumeDialog(true);
    }
  }, []); // Only run on mount

  // Start game and initialize audio
  const startGame = useCallback(() => {
    initializeAudio();
    startGameState();
    generateNewProblem();
  }, [startGameState, generateNewProblem]);

  // Handle answer submission with sound effects
  const handleCheckAnswer = useCallback(() => {
    const modeConfig = GAME_MODE_CONFIG[gameMode];
    const isCorrect = checkAnswer(streak, level, lives);

    if (isCorrect) {
      playSound('correct', settings.soundEnabled);

      // Check for achievement sounds and confetti
      const newStreak = streak + 1;
      if (newStreak === 5 || newStreak === 10) {
        playSound('achievement', settings.soundEnabled);
        triggerConfetti(30, settings.animationsEnabled);
      }
      if (newStreak % 8 === 0) {
        playSound('levelUp', settings.soundEnabled);
        triggerCelebration(settings.animationsEnabled);
      }

      // Refocus input after correct answer
      setTimeout(() => {
        answerInputRef.current?.focus();
      }, TIMINGS.CELEBRATION_DURATION);
    } else {
      playSound('wrong', settings.soundEnabled);

      // In practice mode, don't lose lives
      if (lives === Infinity) {
        // Just continue playing in practice mode
        return;
      }

      // In challenge mode, any mistake ends the game
      if (!modeConfig.allowMistakes || lives <= 1) {
        playSound('gameOver', settings.soundEnabled);
        clearSession(); // Clear saved session on game over

        // Add high score (unless in practice mode)
        if (!modeConfig.noHighScore) {
          addHighScore({
            playerName,
            score,
            level,
            difficulty,
            date: new Date().toISOString(),
          });
        }
      }
    }
  }, [checkAnswer, streak, level, lives, gameMode, settings.soundEnabled, settings.animationsEnabled, playerName, score, difficulty, addHighScore, clearSession]);

  // Handle game reset
  const handleResetGame = useCallback(() => {
    resetGame();
    clearSession();
  }, [resetGame, clearSession]);

  // Handle resume game
  const handleResumeGame = useCallback(() => {
    if (!savedSession) return;

    // Restore game state from session
    setPlayerName(savedSession.playerName);
    toggleOperation('plus'); // Reset and then set correct operations
    toggleOperation('minus');
    toggleOperation('gange');
    if (savedSession.selectedOperations.plus) toggleOperation('plus');
    if (savedSession.selectedOperations.minus) toggleOperation('minus');
    if (savedSession.selectedOperations.gange) toggleOperation('gange');
    setDifficulty(savedSession.difficulty);

    // Note: Would need to add restore functions to useGameState for score, streak, level, lives
    // For now, just start a new game with the same settings

    setShowResumeDialog(false);
    clearSession();
  }, [savedSession, setPlayerName, toggleOperation, setDifficulty, clearSession]);

  // Handle new game (dismiss resume dialog)
  const handleNewGame = useCallback(() => {
    setShowResumeDialog(false);
    setSavedSession(null);
    clearSession();
  }, [clearSession]);

  // Auto-dismiss achievements after 3 seconds
  useEffect(() => {
    if (achievements.length > 0) {
      const timer = setTimeout(() => {
        removeFirstAchievement();
      }, TIMINGS.ACHIEVEMENT_DISPLAY_DURATION || 3000);
      return () => clearTimeout(timer);
    }
  }, [achievements, removeFirstAchievement]);

  // Memoize the next step handler to prevent unnecessary re-renders
  const handleNext = useCallback(() => {
    const success = nextStep();
    if (success && setupStep === 4) {
      startGame();
    }
  }, [nextStep, setupStep, startGame]);

  // Memoize difficulty display
  const difficultyDisplay = useMemo(() => {
    if (difficulty === "let") return "Let 🟢";
    if (difficulty === "medium") return "Medium 🟡";
    return "Svært 🔴";
  }, [difficulty]);

  // Memoize operations display
  const operationsDisplay = useMemo(() => {
    const ops = [];
    if (selectedOperations.plus) ops.push("➕ Plus");
    if (selectedOperations.minus) ops.push("➖ Minus");
    if (selectedOperations.gange) ops.push("✖️ Gange");
    return ops.join(" ");
  }, [selectedOperations]);

  // Check for new high score
  const isNewHighScore = useMemo(() => {
    return highScores.length > 0 && score >= highScores[0].score;
  }, [highScores, score]);

  // Setup Screen
  if (gameState === "setup") {
    return (
      <>
        <SetupScreen
          setupStep={setupStep}
          playerName={playerName}
          selectedOperations={selectedOperations}
          difficulty={difficulty}
          gameMode={gameMode}
          onNameChange={setPlayerName}
          onToggleOperation={toggleOperation}
          onDifficultySelect={setDifficulty}
          onGameModeSelect={setGameMode}
          onNext={handleNext}
          onPrev={prevStep}
        />
        {showResumeDialog && (
          <ResumeGameDialog
            session={savedSession}
            onResume={handleResumeGame}
            onNewGame={handleNewGame}
          />
        )}
      </>
    );
  }

  // Game Over Screen
  if (gameState === "gameOver") {
    return (
      <GameOverScreen
        playerName={playerName}
        score={score}
        level={level}
        streak={maxStreak} // Use max streak instead of current streak
        difficulty={difficulty}
        gameMode={gameMode}
        isNewHighScore={isNewHighScore}
        highScores={highScores}
        onRestart={handleResetGame}
      />
    );
  }

  // Main Game Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 drop-shadow-lg">
            🎯 {playerName}s Matematikspil 🎯
          </h1>

          {/* Timer for Time Attack mode */}
          {GAME_MODE_CONFIG[gameMode]?.hasTimer && (
            <div className="mb-4 flex justify-center">
              <Timer timeRemaining={timeRemaining} />
            </div>
          )}

          <div className="flex justify-center items-center gap-8 text-white text-lg font-semibold">
            <div className="flex items-center gap-2">
              <Star className="text-yellow-300" />
              <span>Score: {score}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="text-orange-300" />
              <span>Streak: {streak}</span>
            </div>
            <div className="flex items-center gap-2">
              <Trophy className="text-yellow-400" />
              <span>Niveau: {level}</span>
            </div>
            {lives !== Infinity && (
              <div className="flex items-center gap-2">
                <Heart className="text-red-400" />
                <span>Liv: {lives}</span>
              </div>
            )}
          </div>
        </div>

        {/* Main Game Area */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center relative overflow-hidden">
          {showCelebration && (
            <div className="absolute inset-0 bg-yellow-200 bg-opacity-30 flex items-center justify-center z-10 pointer-events-none">
              <div className="text-6xl animate-bounce">🎉</div>
            </div>
          )}

          {/* Math Problem */}
          <div className="mb-8">
            <div className="text-6xl font-bold text-gray-800 mb-4">
              {currentProblem.num1} {currentProblem.operator}{" "}
              {currentProblem.num2} = ?
            </div>
          </div>

          {/* Answer Input */}
          <div className="mb-6">
            <input
              ref={answerInputRef}
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={userAnswer}
              onChange={(e) => {
                // Only allow integers (no decimals)
                const value = e.target.value;
                if (value === '' || /^-?\d+$/.test(value)) {
                  setUserAnswer(value);
                }
              }}
              onKeyDown={(e) => {
                // Prevent decimal point and 'e' (scientific notation)
                if (e.key === '.' || e.key === ',' || e.key === 'e' || e.key === 'E') {
                  e.preventDefault();
                }
                if (
                  e.key === "Enter" &&
                  userAnswer.trim() !== "" &&
                  lives > 0
                ) {
                  handleCheckAnswer();
                }
              }}
              className="text-4xl font-bold text-center border-4 border-purple-300 rounded-2xl px-6 py-4 w-48 focus:border-purple-500 focus:outline-none"
              placeholder="?"
              disabled={lives <= 0}
              autoFocus
            />
            <br />
            <button
              onClick={handleCheckAnswer}
              className="mt-6 bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-8 rounded-2xl text-2xl transition-all transform hover:scale-105 disabled:bg-gray-400"
              disabled={lives <= 0 || userAnswer.trim() === ""}
            >
              Tjek Svar! ✅
            </button>
          </div>

          {/* Feedback */}
          {feedback && (
            <div
              className={`text-2xl font-bold mb-4 ${
                feedback.includes("Fantastisk") ||
                feedback.includes("Godt") ||
                feedback.includes("Perfekt") ||
                feedback.includes("Sådan")
                  ? "text-green-600"
                  : feedback.includes("Næsten") || feedback.includes("tæt")
                  ? "text-orange-600"
                  : "text-red-600"
              }`}
            >
              {feedback}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => setShowStatistics(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-xl text-lg transition-all flex items-center gap-2"
            >
              <BarChart3 size={20} />
              Statistik
            </button>
            <button
              onClick={handleResetGame}
              className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-xl text-lg transition-all"
            >
              Tilbage til Indstillinger ⚙️
            </button>
          </div>
        </div>

        {/* Achievements */}
        {achievements.length > 0 && (
          <div className="fixed top-4 right-4 space-y-2 z-50">
            {achievements.map((achievement, index) => (
              <div
                key={`${achievement}-${index}`}
                className="bg-yellow-400 text-black font-bold px-4 py-2 rounded-lg shadow-lg animate-bounce"
              >
                {achievement}
              </div>
            ))}
          </div>
        )}

        {/* Game Info */}
        <div className="mt-8 bg-white bg-opacity-90 rounded-2xl p-6 text-gray-800 text-center shadow-lg">
          <h3 className="text-xl font-bold mb-2">Dit spil:</h3>
          <p className="text-lg">
            <strong>Sværhedsgrad:</strong> {difficultyDisplay}
          </p>
          <p className="text-lg">
            <strong>Regnestykker:</strong> {operationsDisplay}
          </p>
        </div>

        {/* Settings Panel */}
        <div className="mt-6">
          <SettingsPanel
            settings={settings}
            onToggleSound={toggleSound}
            onToggleAnimations={toggleAnimations}
          />
        </div>
      </div>

      {/* Statistics Modal */}
      <StatisticsModal
        isOpen={showStatistics}
        onClose={() => setShowStatistics(false)}
        statisticsHook={statisticsHook}
      />
    </div>
  );
};

export default Matematikspillet;
